import { usePointOfInterestsCountByInspectionPriorityQuery } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { useWindowSize } from '@/hooks/useWindowSize';
import { POI_INSPECTION_PRIORITY_COLOR_MAP } from '@/theme/colors';
import { titleCase } from '@/utils';
import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { PieChartWidget } from '../shared/PieChartWidget';
import { getPriorityColors } from '../styles';
import { PoiSummaryWidgetsProps } from '../StatusWidget/StatusWidget';

export const PriorityWidget = ({ queryInput, widgetWidth = '0' }: PoiSummaryWidgetsProps) => {
  const allPointOfInterestTemplates = useAtomValue(state.allPointOfInterestTemplates);

  const screenSize = useWindowSize();

  const type = queryInput?.type;

  const inspectionPriorityKeys = useMemo(() => {
    if (type) {
      const selectedPoiTemplate = allPointOfInterestTemplates?.find(
        (template) => template?.name === titleCase(type)
      );

      return selectedPoiTemplate?.fields
        .filter((field) => field.name.toLowerCase() === 'inspection priority')
        .map((field) => field.key);
    }

    return allPointOfInterestTemplates?.flatMap((template) =>
      template.fields
        .filter((field) => field.name.toLowerCase() === 'inspection priority')
        .map((field) => field.key)
    );
  }, [allPointOfInterestTemplates, type]);

  const { data: queryResponse, loading } = usePointOfInterestsCountByInspectionPriorityQuery({
    variables: {
      input: {
        ...queryInput,
        priorityFieldKeys: (inspectionPriorityKeys ?? []).filter((k): k is string => k !== null),
        structureId: queryInput.structureIds?.[0] || '',
      },
    },
  });

  const legendKeys = useMemo(() => {
    return Object.keys(POI_INSPECTION_PRIORITY_COLOR_MAP).map((key) => titleCase(key));
  }, []);

  const data = useMemo(() => {
    const inspectionPriorityCount = queryResponse?.pointOfInterestsCountByInspectionPriority;
    return inspectionPriorityCount?.length === 0
      ? [{ key: 'N/A', count: 1 }]
      : inspectionPriorityCount
          ?.map((inspectionPriority) => ({
            count: inspectionPriority?.count,
            key: inspectionPriority?.key,
          }))
          .sort((a, b) => {
            return legendKeys.indexOf(a.key ?? '') - legendKeys.indexOf(b.key ?? '');
          });
  }, [legendKeys, queryResponse?.pointOfInterestsCountByInspectionPriority]);

  const size = useMemo(() => {
    const { width } = screenSize;
    if (width && width <= 1728) {
      return {
        outerRadius: 52,
        innerRadius: 43,
      };
    }
    return {
      outerRadius: 75,
      innerRadius: 62,
    };
  }, [screenSize]);
  const scale = useMemo(() => {
    const { width } = screenSize;
    if (width && width <= 1728) {
      return 0.7;
    }
    return 1;
  }, [screenSize]);

  const { highCount, highText } = useMemo(() => {
    return (
      legendKeys
        .map((key) => ({
          highText: key,
          highCount: data?.find((item) => item.key === key)?.count ?? 0,
        }))
        .find((key) => key.highCount) ?? { highCount: 0, highText: legendKeys[0] }
    );
  }, [data, legendKeys]);

  return (
    <PieChartWidget
      widgetWidth={widgetWidth}
      loading={loading}
      chartScale={scale}
      data={data}
      chartSize={size}
      colorMapper={getPriorityColors}
      title="Inspection Priority"
      centerText={highText}
      centerValue={highCount}
      legendKeys={legendKeys}
    />
  );
};
